import React from "react";
import { formatUtcDateTime, parseUtcDate } from "../utils/dateTime";

// Shared Fortinet diagnostic display pieces: the Fortinet card in Integration Settings and the
// Super Admin page's Fortinet diagnostics panel render the same last-scan line and trace switch.

const SCAN_LABELS = { Succeeded: "Succeeded", Partial: "Partially succeeded", Failed: "Failed" };
const SCAN_COLORS = { Succeeded: "text-green-600", Partial: "text-amber-600", Failed: "text-red-600" };

/** "Last scan: {time} · {Succeeded|Partially succeeded|Failed}" + message, or "Not scanned yet." */
export function FortinetLastScan({ lastScannedAt, lastScanStatus, lastScanMessage, className = "px-4 pb-3" }) {
  const scanLabel = SCAN_LABELS[lastScanStatus];
  const scanColor = SCAN_COLORS[lastScanStatus] || "text-gray-600";
  return (
    <div className={className} data-testid="fortinet-last-scan">
      {lastScannedAt ? (
        <>
          <p className="text-xs text-gray-600">
            Last scan: {formatUtcDateTime(lastScannedAt)}
            {scanLabel && <> · <span className={`font-semibold ${scanColor}`}>{scanLabel}</span></>}
          </p>
          {lastScanMessage && <p className="text-xs text-gray-400 mt-0.5">{lastScanMessage}</p>}
        </>
      ) : (
        <p className="text-xs text-gray-500">Not scanned yet.</p>
      )}
    </div>
  );
}

/**
 * A Test connection result: { message } (an error), or { diagnostics: [{source, ok, detail}],
 * managedDevices, signalCount } as one ✓/✗ row per call plus a totals line.
 */
export function FortinetTestResult({ result, className = "mx-4 mb-4 pt-2 border-t border-orange-200 space-y-1" }) {
  return (
    <div className={className} data-testid="fortinet-test-result">
      {result.message ? (
        <div className="text-xs text-red-600">{result.message}</div>
      ) : (
        <>
          {result.diagnostics.map((r, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs">
              <span className={`font-bold shrink-0 ${r.ok === true ? "text-green-600" : r.ok === false ? "text-red-600" : "text-gray-400"}`}>
                {r.ok === true ? "✓" : r.ok === false ? "✗" : "•"}
              </span>
              <span className="font-semibold text-gray-700 shrink-0">{r.source}:</span>
              <span className="text-gray-600">{r.detail}</span>
            </div>
          ))}
          <div className="text-xs text-gray-500 pt-1">
            {result.managedDevices != null && `${result.managedDevices} managed devices · `}
            {`${result.signalCount} findings would be recorded`}
          </div>
        </>
      )}
    </div>
  );
}

/** Whether tracing is on now, and its end time formatted for display. */
export function fortinetTraceState(traceUntil) {
  const traceDate = parseUtcDate(traceUntil);
  const traceOn = !!traceDate && traceDate.getTime() > Date.now();
  return { traceOn, traceTime: traceOn ? traceDate.toLocaleString() : "" };
}

/**
 * The SuperAdmin-only "Diagnostic tracing (24 h)" switch (PLAN D12/D19). With canManage false it
 * renders only the read-only "Diagnostic tracing is on until {time}" line, and nothing when off.
 */
export function FortinetTraceSwitch({ traceUntil, canManage, saving, onChange, className = "px-4 pb-4 border-t border-gray-100 pt-3", readOnlyClassName = "px-4 pb-4 text-xs text-gray-500" }) {
  const { traceOn, traceTime } = fortinetTraceState(traceUntil);
  if (canManage) {
    return (
      <div className={className}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={traceOn} disabled={saving}
            onChange={e => onChange(e.target.checked)}
            className="rounded accent-orange-500" />
          <span className="text-xs font-medium text-gray-600">Diagnostic tracing (24 h)</span>
        </label>
        <p className="text-xs text-gray-400 mt-1 ml-5">
          {traceOn
            ? `Logs Fortinet request and response bodies (up to 32 KB each, secrets and personal data masked) to Axaris support logs until ${traceTime}.`
            : "Logs Fortinet request and response bodies (up to 32 KB each, secrets and personal data masked) to Axaris support logs for 24 hours."}
        </p>
      </div>
    );
  }
  return traceOn ? (
    <div className={readOnlyClassName}>Diagnostic tracing is on until {traceTime}</div>
  ) : null;
}
